// First-touch marketing attribution capture.
//
// On every page load we snapshot UTM / fbclid / gclid / referrer / landing_page
// from the current URL. First-touch semantics: once written to localStorage
// under `gf_attribution`, we NEVER overwrite it — so the source that brought
// the user in gets credit for their eventual signup + purchase, not whatever
// tab they were on when they finally decided to click Register.

const STORAGE_KEY = "gf_attribution";
const TRACKED_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "fbclid", "gclid",
];

/**
 * Read the current URL params + referrer and, if this is the FIRST time we've
 * seen this browser AND there's something worth capturing, persist to storage.
 * Idempotent — safe to call on every route change.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem(STORAGE_KEY)) return; // first-touch: never overwrite
    const params = new URLSearchParams(window.location.search || "");
    const captured = {};
    for (const key of TRACKED_PARAMS) {
      const v = params.get(key);
      if (v) captured[key] = v.slice(0, 200);
    }
    const referrer = document.referrer || "";
    // Only persist if there's a UTM / click id, OR referrer is an external site.
    const hasClickAttribution = Object.keys(captured).length > 0;
    const hasExternalReferrer =
      referrer && !referrer.startsWith(window.location.origin);
    if (!hasClickAttribution && !hasExternalReferrer) return;
    captured.landing_page = (window.location.pathname + window.location.search).slice(0, 500);
    captured.referrer = referrer.slice(0, 500);
    captured.first_seen_at = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // localStorage disabled / quota / SSR — silently skip.
  }
}

/** Return the persisted attribution object, or null if nothing was captured. */
export function getAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Test-only helper. Not used in production code. */
export function _clearAttribution() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
